import { apiClient } from './client'
import { ApiError } from './errors'

export interface Product {
  id: number
  name: string
  description: string
  sku: string
  tradable: boolean
  unitType: string
}

export interface SpotPrice {
  id: number
  midpointPerMg: number
  askPricePerMg: number
  bidPricePerMg: number
  spotTime: string
}

export interface HistoricalSpotPrice {
  id: number
  midpointPerG: number
  spotDate: string
}

export interface ProductPerformance {
  currentValue: number
  netProfit: number
  quantityOwned: number
  periodInvestedInDays: number
  totalCost: number
  totalReturn: number
  totalReturnAsPercentage: number
  unit: string
  spotPrice: SpotPrice
}

type ProductPerformanceResponse = Partial<ProductPerformance> & {
  current_value?: number | string
  total_quantity_mg?: number | string
  cost_basis?: number | string
  gain_loss?: number | string
  gain_loss_pct?: number | string | null
}

function mapProductPerformance(
  data: ProductPerformanceResponse
): ProductPerformance {
  const currentValue = Number(data.currentValue ?? data.current_value ?? 0)
  const totalCost = Number(data.totalCost ?? data.cost_basis ?? 0)
  const totalReturn = Number(data.totalReturn ?? data.gain_loss ?? currentValue)

  return {
    currentValue,
    netProfit: Number(data.netProfit ?? totalReturn),
    quantityOwned: Number(data.quantityOwned ?? data.total_quantity_mg ?? 0),
    periodInvestedInDays: Number(data.periodInvestedInDays ?? 0),
    totalCost,
    totalReturn,
    totalReturnAsPercentage: Number(
      data.totalReturnAsPercentage ?? data.gain_loss_pct ?? 0
    ),
    unit: data.unit ?? 'mg',
    spotPrice: data.spotPrice ?? {
      id: 0,
      midpointPerMg: 0,
      askPricePerMg: 0,
      bidPricePerMg: 0,
      spotTime: ''
    }
  }
}

export interface ProductPerformanceSummary {
  currentValue: number
  totalCost: number
  totalReturn: number
  totalReturnAsPercentage: number
  product: Product
}

export interface SecurityOwned {
  id: number
  quantityMg: number
  security: {
    id: number
    serialNumber: string
    vault: { id: number; name: string }
  }
}

export interface TcAcceptance {
  productId: number
  accepted: boolean
}

export interface Vault {
  id: number
  name: string
  email?: string
  dateOfBirth?: string
  relationship?: string
  balance?: number
  isDefault?: boolean
}

type VaultResponse = Vault & {
  date_of_birth?: string
  is_default?: boolean
}

function mapVault(vault: VaultResponse): Vault {
  return {
    ...vault,
    dateOfBirth: vault.dateOfBirth ?? vault.date_of_birth,
    isDefault: vault.isDefault ?? vault.is_default
  }
}

function toVaultRequest(data: Partial<Vault>) {
  return {
    name: data.name,
    email: data.email,
    date_of_birth: data.dateOfBirth,
    relationship: data.relationship
  }
}

export interface MarketOrder {
  id: number
  createdAt: string
  orderType: string
  quantityMg: number
  pricePerUnit: number
}

type ProductResponse = Partial<Product> & {
  unit_type?: string
}

function mapProduct(p: ProductResponse): Product {
  return {
    id: p.id as number,
    name: p.name as string,
    description: p.description ?? '',
    sku: p.sku as string,
    tradable: p.tradable ?? false,
    // BE /products returns unit_type (snake); the app reads unitType everywhere.
    unitType: p.unitType ?? p.unit_type ?? 'mg'
  }
}

export async function fetchProducts(): Promise<Product[]> {
  try {
    const response = await apiClient.get<ProductResponse[]>('products')
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const body = response.data as any
    const list = (body?.data ?? body) as ProductResponse[]
    return (list ?? []).map(mapProduct)
  } catch (err) {
    throw ApiError.fromAxiosError(err)
  }
}

export async function fetchSpotPrice(productId: number): Promise<SpotPrice> {
  try {
    const response = await apiClient.get<SpotPrice>(
      `spot_price/latest/${productId}`
    )
    return response.data
  } catch (err) {
    throw ApiError.fromAxiosError(err)
  }
}

export async function fetchHistoricalSpotPrices(
  productId: number,
  timeframe: string
): Promise<HistoricalSpotPrice[]> {
  try {
    const response = await apiClient.get<HistoricalSpotPrice[]>(
      `spot_price/historical/${productId}`,
      { params: { timeframe } }
    )
    return response.data
  } catch (err) {
    throw ApiError.fromAxiosError(err)
  }
}

function getVaultParams(vaultId?: number | null) {
  return vaultId != null && Number.isFinite(vaultId)
    ? { vault_id: vaultId }
    : undefined
}

export async function fetchProductPerformance(
  productId: number,
  vaultId?: number | null
): Promise<ProductPerformance> {
  try {
    const response = await apiClient.get<ProductPerformanceResponse>(
      `products/${productId}/performance`,
      { params: getVaultParams(vaultId) }
    )
    return mapProductPerformance(response.data)
  } catch (err) {
    throw ApiError.fromAxiosError(err)
  }
}

export async function fetchPerformanceSummaries(
  vaultId?: number | null
): Promise<ProductPerformanceSummary[]> {
  try {
    const response = await apiClient.get<ProductPerformanceSummary[]>(
      'products/performance_summaries',
      { params: getVaultParams(vaultId) }
    )
    return response.data
  } catch (err) {
    throw ApiError.fromAxiosError(err)
  }
}

export async function fetchSecuritiesOwned(
  productId: number
): Promise<SecurityOwned[]> {
  try {
    const response = await apiClient.get<SecurityOwned[]>('securities/owned', {
      params: { product_id: productId }
    })
    return response.data
  } catch (err) {
    throw ApiError.fromAxiosError(err)
  }
}

export async function fetchTcAcceptance(
  productId: number
): Promise<TcAcceptance> {
  try {
    const response = await apiClient.get<TcAcceptance>(
      `products/${productId}/tc_acceptance`
    )
    return response.data
  } catch (err) {
    throw ApiError.fromAxiosError(err)
  }
}

export async function acceptTcAcceptance(productId: number): Promise<void> {
  try {
    await apiClient.post(`products/${productId}/tc_acceptance`)
  } catch (err) {
    throw ApiError.fromAxiosError(err)
  }
}

export async function fetchVault(vaultId: number): Promise<Vault> {
  try {
    const response = await apiClient.get<VaultResponse>(`vaults/${vaultId}`)
    return mapVault(response.data)
  } catch (err) {
    throw ApiError.fromAxiosError(err)
  }
}

export async function createVault(data: {
  name: string
  email?: string
  dateOfBirth?: string
  relationship?: string
}): Promise<Vault> {
  try {
    const response = await apiClient.post<VaultResponse>(
      'vaults',
      toVaultRequest(data)
    )
    return mapVault(response.data)
  } catch (err) {
    throw ApiError.fromAxiosError(err)
  }
}

export async function updateVault(
  vaultId: number,
  data: Partial<Vault>
): Promise<Vault> {
  try {
    const response = await apiClient.put<VaultResponse>(
      `vaults/${vaultId}`,
      toVaultRequest(data)
    )
    return mapVault(response.data)
  } catch (err) {
    throw ApiError.fromAxiosError(err)
  }
}

export async function activateVault(vaultId: number): Promise<Vault> {
  try {
    const response = await apiClient.post<VaultResponse>(
      `vaults/${vaultId}/activate`
    )
    return mapVault(response.data)
  } catch (err) {
    throw ApiError.fromAxiosError(err)
  }
}

type MarketOrderResponse = Partial<MarketOrder> & {
  created_at?: string
  order_type?: string
  // BE MarketOrder.amount is the quantity in mg; price_per_unit is the unit price.
  amount?: number
  quantity_mg?: number
  price_per_unit?: number | string
}

function mapMarketOrder(o: MarketOrderResponse): MarketOrder {
  return {
    id: o.id as number,
    createdAt: o.createdAt ?? o.created_at ?? '',
    orderType: o.orderType ?? o.order_type ?? '',
    quantityMg: Number(o.quantityMg ?? o.quantity_mg ?? o.amount ?? 0),
    pricePerUnit: Number(o.pricePerUnit ?? o.price_per_unit ?? 0)
  }
}

export async function fetchMarketOrders(
  productId: number
): Promise<MarketOrder[]> {
  try {
    // BE /market_orders/{product_id} returns rows in snake_case
    // (created_at/order_type/amount/price_per_unit). Map to the camelCase the
    // TableTransactionHistory rows read (createdAt/orderType/quantityMg/pricePerUnit).
    const response = await apiClient.get<MarketOrderResponse[]>(
      `market_orders/${productId}`
    )
    const data = response.data
    return Array.isArray(data) ? data.map(mapMarketOrder) : []
  } catch (err) {
    throw ApiError.fromAxiosError(err)
  }
}
